import { Link } from "react-router-dom";
import { Sun, Moon, PaintRoller, Check } from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { useTheme } from "@/context/ThemeContext";

const themes = [
  {
    key: "brutal",
    name: "Brutalist Dark",
    tag: "DEFAULT",
    desc: "Negro profundo con acento naranja Blaze. Tipografía Bricolage Grotesque. Para la comunidad que empuja hacia adelante.",
    icon: Moon,
    palette: ["#050505", "#FF3E00", "#F4F4F5", "#27272A"],
  },
  {
    key: "editorial",
    name: "Editorial Light",
    tag: "PREMIUM",
    desc: "Luz elegante de revista. Crema + tinta. Para artistas con temperamento clásico. Incluido en tiers Coleccionista+.",
    icon: Sun,
    palette: ["#FAFAF5", "#8B0000", "#0A0A0A", "#E5E5E0"],
  },
];

export default function Settings() {
  const { theme, setTheme } = useTheme();

  return (
    <Layout wide>
      <div data-testid="settings-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
            AJUSTES · APARIENCIA
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Tu <span className="text-[#FF3E00]">estética</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-2xl leading-relaxed">
            // ELIGE CÓMO TE PRESENTAS A LA COMUNIDAD. EL TEMA SE APLICA A TODA
            LA PLATAFORMA Y AL PERFIL PÚBLICO DE TU CUENTA.
          </p>
        </header>

        {/* Theme picker */}
        <section className="p-4 md:p-6">
          <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4 flex items-center gap-2">
            <PaintRoller size={18} weight="fill" className="text-[#FF3E00]" />
            Tema visual
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {themes.map((t) => {
              const Icon = t.icon;
              const active = theme === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`theme-${t.key}`}
                  onClick={() => setTheme(t.key)}
                  className={`text-left border relative group transition-all ${
                    active
                      ? "border-[#FF3E00] bg-[#FF3E00]/5"
                      : "border-[#27272A] bg-[#0a0a0a] hover:border-[#F4F4F5]"
                  }`}
                >
                  {/* Preview hero */}
                  <div
                    className="relative aspect-[21/10] overflow-hidden"
                    style={{
                      background:
                        t.key === "brutal" ? "#050505" : "#FAFAF5",
                      color: t.key === "brutal" ? "#F4F4F5" : "#0A0A0A",
                    }}
                  >
                    <div className="absolute inset-0 p-6 flex flex-col justify-between">
                      <div
                        className="font-mono text-[9px] tracking-[0.3em]"
                        style={{
                          color: t.key === "brutal" ? "#FF3E00" : "#8B0000",
                        }}
                      >
                        ELARTE / NET · {t.tag}
                      </div>
                      <div>
                        <div
                          className="font-display font-black text-4xl md:text-5xl tracking-tighter uppercase leading-[0.9]"
                          style={{
                            color: t.key === "brutal" ? "#F4F4F5" : "#0A0A0A",
                          }}
                        >
                          Esta semana
                          <br />
                          <span
                            style={{
                              color:
                                t.key === "brutal" ? "#FF3E00" : "#8B0000",
                            }}
                          >
                            en el ecosistema
                          </span>.
                        </div>
                        <div
                          className="font-mono text-[9px] tracking-wider mt-3 opacity-70"
                          style={{
                            color: t.key === "brutal" ? "#A1A1AA" : "#525252",
                          }}
                        >
                          13 COLABS · 21 OBRAS · €27K EN PREMIOS
                        </div>
                      </div>
                    </div>
                    {active ? (
                      <div className="absolute top-3 right-3 bg-[#FF3E00] text-white h-8 w-8 inline-flex items-center justify-center">
                        <Check size={16} weight="bold" />
                      </div>
                    ) : null}
                  </div>

                  {/* Meta */}
                  <div className="p-4 border-t border-[#27272A]">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          size={16}
                          weight="fill"
                          className={
                            active ? "text-[#FF3E00]" : "text-[#A1A1AA]"
                          }
                        />
                        <span className="font-display font-black text-lg uppercase tracking-tight">
                          {t.name}
                        </span>
                      </div>
                      <span
                        className={`font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 ${
                          t.tag === "PREMIUM"
                            ? "bg-[#FFD600] text-black"
                            : "bg-[#27272A] text-[#A1A1AA]"
                        }`}
                      >
                        {t.tag}
                      </span>
                    </div>
                    <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider leading-relaxed mt-3">
                      {t.desc}
                    </p>

                    {/* Palette */}
                    <div className="flex items-center gap-2 mt-4">
                      {t.palette.map((c) => (
                        <span
                          key={c}
                          className="h-5 w-5 border border-[#27272A]"
                          style={{ background: c }}
                          title={c}
                        />
                      ))}
                      <span className="font-mono text-[9px] text-[#A1A1AA] tracking-wider ml-auto">
                        {active ? "ACTIVO" : "Seleccionar"}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Premium CTA */}
          {theme !== "editorial" ? (
            <div className="mt-6 border border-dashed border-[#FFD600] bg-[#FFD600]/5 p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[10px] text-[#FFD600] tracking-[0.3em] mb-1">
                  TEMA PREMIUM
                </div>
                <div className="font-display font-black text-lg">
                  Desbloquea Editorial Light con Coleccionista+
                </div>
                <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-1">
                  Durante el mockup puedes probarlo libremente.
                </div>
              </div>
              <Link
                to="/artista/mar-quintero"
                className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FFD600] text-black px-4 py-3 font-bold"
              >
                Ver tiers
              </Link>
            </div>
          ) : null}
        </section>

        {/* Other prefs mockup */}
        <section className="p-4 md:p-6 border-t border-[#27272A]">
          <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
            Preferencias
          </h2>
          <ul className="border border-[#27272A]">
            <Pref label="Movimiento reducido" desc="Menos animaciones · mejor para mentes neuro-divergentes" />
            <Pref label="Autoplay de videos" desc="Reproducir video en el feed al pasar el cursor" on />
            <Pref label="Notificaciones email" desc="Boletín semanal + resumen de actividad" on />
            <Pref label="Idioma" desc="Español (España) · EN · PT próximamente" select />
            <Pref label="Mostrar NSFW" desc="Obras marcadas como sensibles" />
          </ul>
        </section>
      </div>
    </Layout>
  );
}

function Pref({ label, desc, on = false, select = false }) {
  return (
    <li className="flex items-center justify-between gap-4 p-4 border-b border-[#27272A] last:border-b-0">
      <div className="min-w-0">
        <div className="font-display font-bold text-sm">{label}</div>
        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
          {desc}
        </div>
      </div>
      {select ? (
        <select className="bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-3 py-2 font-mono text-xs">
          <option>ES</option>
          <option>EN</option>
          <option>PT</option>
        </select>
      ) : (
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" defaultChecked={on} className="sr-only peer" />
          <span className="w-11 h-6 bg-[#27272A] peer-checked:bg-[#FF3E00] transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      )}
    </li>
  );
}
